import React, { forwardRef, useContext, useMemo } from 'react';

import { cva } from 'class-variance-authority';
import { defaultSchema } from 'rehype-sanitize';
import { Streamdown, defaultRehypePlugins, defaultRemarkPlugins } from 'streamdown';

import { buildMessageEntitySnapshots } from '../lib/entitySnapshots';
import { remarkEntityPlugin } from '../lib/remarkEntityPlugin';
import { escapeEntityAttr, richdownEntityRefRegex } from '../lib/richdownEntityRefRegex';
import { cn } from '../lib/utils';

import { EntitySnapshotContext } from '../contexts/EntitySnapshotContext';
import { useEntityReplacer } from '../hooks/useEntityReplacer';

const variants = cva('max-w-full', {
  variants: {
    variant: {
      'agent-thinking': 'streamdown-thinking text-gray-400 dark:text-neutral-400',
      'agent-response': 'streamdown-response text-black dark:text-white',
      'markdown-document': 'streamdown-markdown-document text-gray-950 dark:text-neutral-100',
    },
  },
  defaultVariants: {
    variant: 'agent-response',
  },
});

const StreamdownWithEntities = forwardRef(({ children, variant, ...props }, ref) => {
  const globalEntitySnapshots = useContext(EntitySnapshotContext);

  // build entity snapshots for the current text content
  // extract entity references from markdown and convert to HTML format for buildMessageEntitySnapshots
  const messageEntitySnapshots = useMemo(() => {
    if (typeof children !== 'string') return {};
    // convert [type|id] or legacy [type:id] to <entity> for snapshot building (matches remarkEntityPlugin)
    const html = children.replace(richdownEntityRefRegex(), (match, type, idPipe, idColon) => {
      const id = idPipe ?? idColon;
      const t = type.toLowerCase();
      if (['profile', 'channel', 'spammer'].includes(t)) {
        return `<entity data-type="${t}" data-target="${escapeEntityAttr(id)}"></entity>`;
      }
      if (t === 'emoji') {
        return `<entity data-type="emoji" data-unified="${escapeEntityAttr(id)}"></entity>`;
      }
      return match;
    });
    return buildMessageEntitySnapshots(html, globalEntitySnapshots);
  }, [children, globalEntitySnapshots]);

  // use shared entity replacer logic to create component mapper
  const { createEntityComponent } = useEntityReplacer(messageEntitySnapshots);

  // component mapper for Streamdown's components prop
  // createEntityComponent expects a DOM element, so we create a mock element from props
  const components = useMemo(
    () => ({
      entity: (props) => {
        const mockElement = {
          getAttribute: (name) => {
            const propName = name.replace('data-', '');
            return props[`data-${propName}`] || props[propName];
          },
        };
        return createEntityComponent(mockElement);
      },
      // custom link component that opens external links directly without showing dialog
      a: ({ href, children, ...props }) => {
        const isExternal = href?.toLowerCase().startsWith('http://') || href?.toLowerCase().startsWith('https://');
        return (
          <a
            {...props}
            className="cursor-pointer underline"
            href={href}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
          >
            {children}
          </a>
        );
      },
    }),
    [createEntityComponent],
  );

  // extend default sanitize schema with entity tags and attributes
  // start with defaultSchema (which has all security defaults) and only add what we need
  const sanitizeSchema = useMemo(() => {
    return {
      ...defaultSchema,
      // add entity to allowed tags while preserving all default safe tags
      tagNames: [...defaultSchema.tagNames, 'entity'],
      // add entity attributes while preserving all default safe attributes
      attributes: {
        ...defaultSchema.attributes,
        entity: ['dataType', 'dataTarget', 'dataId', 'dataName', 'dataUnified'],
      },
    };
  }, []);

  return (
    <Streamdown
      className={cn(variants({ variant }))}
      controls={false}
      components={components}
      remarkPlugins={[defaultRemarkPlugins.gfm, remarkEntityPlugin]}
      rehypePlugins={[defaultRehypePlugins.raw, [defaultRehypePlugins.sanitize[0], sanitizeSchema], defaultRehypePlugins.harden]}
      ref={ref}
      {...props}
    >
      {children}
    </Streamdown>
  );
});
StreamdownWithEntities.displayName = 'StreamdownWithEntities';

export { StreamdownWithEntities, variants };
